import type { CustomerAllergies } from '@/features/customers/api';
import type { XeroAccountChoice } from '@/features/xero/api';
import { api } from '@/lib/api';

/*
  Orders. dbo.Order and dbo.OrderDetail, the tables the mobile API and the retired portal read
  right now.

  The register is 4,135 live orders, so like the product register it is paged, sorted and counted
  on the server: `list` takes the sort and the page and answers one page. The filter drawer
  (ALS-56) adds its fields to this same query, and the export needs no change when it does,
  because the export is this endpoint walked in bigger pages.

  Two of a row's statuses are derived rather than stored. The payment status comes out of four
  columns the way the legacy grid's OrderSummaryFetch computes it, and the note under the order
  date is the redesign's own addition. Both are decided on the server; this file only names the
  values it can receive.
*/

/** Where an order is, as dbo.mStatus holds it for the order module. */
export type OrderStatus =
    'Unknown' | 'Placed' | 'InProgress' | 'Completed' | 'Cancelled' | 'Deleted';

/**
 * How far the order has been paid, derived from four columns on the order row. Refunded is the
 * one reading of our own: a cancelled order that was paid (ADR 0049), which the legacy's own
 * derivation calls Fully paid.
 */
export type PaymentStatus =
    'NotPaid' | 'DepositUnpaid' | 'DepositPaid' | 'PartiallyPaid' | 'FullyPaid' | 'Refunded';

/**
 * Whether the suppliers have paid the commission an order earned them (ALS-288). One per order,
 * set by hand, and only an order with a product that earns commission has one: everywhere else it
 * is null, which is no status rather than Not paid.
 */
export type CommissionStatus = 'NotPaid' | 'Paid';

/** What a line's status means for the dot on its thumbnail. */
export type OrderLineTone = 'InFlight' | 'Done' | 'Attention' | 'Adverse' | 'Neutral';

/**
 * A line's status as the design names it, which the server reads off each status row's
 * (StatusType, ActionType) rather than its name (ALS-61). The eighteen of the design's flow, then
 * the retired ones old lines still carry: Cancelled and Refunded were last set in 2022 and 2023,
 * Exchange and the six trunk-show rows never on a live line. Unknown is a row this build does not
 * know, which reads by its legacy name.
 */
export type OrderLineStatus =
    | 'Pending'
    | 'AwaitingPayment'
    | 'Confirmed'
    | 'Secured'
    | 'Received'
    | 'Shipped'
    | 'Delivered'
    | 'ExchangeRequested'
    | 'ExchangeInProgress'
    | 'ExchangeShipped'
    | 'ExchangeDelivered'
    | 'CancellationRequested'
    | 'CancellationConfirmed'
    | 'CancellationRefunded'
    | 'ReturnRequested'
    | 'RefundInProgress'
    | 'RefundCompleted'
    | 'Deleted'
    | 'TrunkShow'
    | 'Exchange'
    | 'Cancelled'
    | 'Refunded'
    | 'Unknown';

/** Which of the three shapes the second line under the order date is, if any. */
export type OrderDateNoteKind = 'None' | 'Due' | 'Delivered' | 'Cancelled';

/** The sortable columns, as the API spells them on the query string. */
export type OrderSortKey = 'date' | 'customer' | 'net' | 'payment' | 'status';

export interface OrderLineThumb {
    productId: number;
    name: string;
    /** The line's status as mStatus names it, which reads only where the design has no name. */
    status: string;
    /** What the status means, which picks the design's name for the tooltip and screen reader. */
    lineStatus: OrderLineStatus;
    tone: OrderLineTone;
    /** The product's primary category, which picks the glyph when there is no picture. */
    category: string | null;
    thumbnailUrl: string | null;
}

export interface OrderListItem {
    id: number;
    orderNo: string;
    customerId: number;
    customer: string;
    orderDate: string;
    status: OrderStatus;
    /** What mStatus calls the status, for a row whose status this build does not know. */
    statusName: string;
    payment: PaymentStatus;
    /** Null for an order that earns no commission. */
    commissionStatus: CommissionStatus | null;
    net: number;
    currency: string;
    /** The net in GBP, which is what sorting by amount compares. */
    netBase: number;
    /** Live lines on the order, however many of them came back in `lines`. */
    itemCount: number;
    noteKind: OrderDateNoteKind;
    /** An ISO day, or null when the kind is None. */
    noteDate: string | null;
    /** The first few lines, for the thumbnail strip. Never more than four. */
    lines: OrderLineThumb[];
    /** The invoice number, null until the order is invoiced: the first of the dashboard's Documents. */
    invoiceNo: string | null;
    /** The DHL waybills of the order's live outbound parcels, in parcel order: the dashboard's Documents. */
    waybills: string[];
}

/** One page of the register: `total` is what the filters matched, `registerTotal` every live order. */
export interface OrderPage {
    total: number;
    registerTotal: number;
    page: number;
    pageSize: number;
    items: OrderListItem[];
}

/** One product on an opened order, with the nine facts the design prints under it (ALS-58). */
export interface OrderOverviewLine {
    id: number;
    productId: number;
    name: string;
    brand: string | null;
    /** The product's code, printed under its name. */
    code: string | null;
    /** The product's own description, plain text that may carry line breaks. */
    description: string | null;
    category: string | null;
    thumbnailUrl: string | null;
    /** Who this line was bought from: the line's supplier, not the product's current one. */
    supplier: string | null;
    /** Size and colour, "M · Black"; null for a service, which has neither. */
    variant: string | null;
    qty: number;
    /** The unit sale price, in the order's currency. */
    unitPrice: number;
    commission: boolean;
    /** The percentage the commission resolves to on the order date, when a rate applies. */
    commissionRate: number | null;
    origin: string | null;
    status: string;
    lineStatus: OrderLineStatus;
    tone: OrderLineTone;
    /** Whether the piece is in the client's wardrobe, by the rule her app reads. */
    inWardrobe: boolean;
    /** An ISO day. */
    estimatedDelivery: string | null;
    preOrder: boolean;
    dropShip: boolean;
}

/**
 * What a register row opens into: every live line, then the order's own facts. Where the design
 * names an order fact the schema keeps per line (tracking, assignee, reason) the server folds
 * the lines' values, so each is a list.
 */
export interface OrderOverview {
    id: number;
    orderNo: string;
    currency: string;
    invoiceType: string | null;
    invoiceNo: string | null;
    /** An ISO day. */
    invoiceDate: string | null;
    reference: string | null;
    amountCard: number;
    amountWallet: number;
    /** The shipping address, one part per entry, as the legacy invoice composes it. */
    shippingAddress: string[];
    phone: string | null;
    shippingCountry: string | null;
    billingCountry: string | null;
    customerCountry: string | null;
    tracking: string[];
    /** The latest estimate among the lines: when the last piece is expected. */
    estimatedDelivery: string | null;
    /** The team's delivery notes, OrderDetail.Remark, folded from the lines like tracking. */
    deliveryComments: string[];
    assignedTo: string[];
    /** The most recent assignment of any line. */
    assignedOn: string | null;
    cancellationReasons: string[];
    /**
     * The DHL waybills of the order's live parcels, in parcel order: the Documents fact (ALS-262).
     * The invoice is listed before them, by `invoiceNo` (ALS-66).
     */
    waybills: OrderWaybill[];
    lines: OrderOverviewLine[];
}

/** One parcel's waybill as the Documents fact opens it. */
export interface OrderWaybill {
    waybill: string;
    /** The parcel's reference, the viewer's subtitle. */
    reference: string;
    /**
     * Its label PDF, opened in the document viewer. Null for a label DHL made after a lost answer,
     * whose PDF cannot be fetched again: the fact links to the Shipping & returns tab instead.
     */
    labelUrl: string | null;
}

/** One row of a drawer list: an id and what to call it. */
export interface NamedRow {
    id: number;
    name: string;
}

/** A line status for the filter drawer: its row, its legacy name, and what it means. */
export interface LineStatusRow extends NamedRow {
    status: OrderLineStatus;
}

/** Every list the filter drawer selects over, bar the products, which are their own call. */
export interface OrderLookups {
    customers: NamedRow[];
    relationshipManagers: NamedRow[];
    assignedTo: NamedRow[];
    saleSources: NamedRow[];
    invoiceTypes: NamedRow[];
    lineStatuses: LineStatusRow[];
    brands: NamedRow[];
    suppliers: NamedRow[];
    taxes: NamedRow[];
    sizeTypes: NamedRow[];
    sizes: NamedRow[];
    categoryGroups: NamedRow[];
    categories: NamedRow[];
    secondaryCategories: NamedRow[];
    tertiaryCategories: NamedRow[];
}

/**
 * The register's query: the sort, the page, and the drawer's thirty-one filters. Every one optional,
 * and a filter the drawer left neutral is absent rather than sent as "All".
 */
export interface OrderListQuery {
    /** The page header's search, over orders, clients and products. */
    q?: string;
    orderNo?: string;
    invoiceNo?: string;
    reference?: string;
    tracking?: string;
    customerId?: number;
    rmId?: number;
    assignedToId?: number;
    invoiceTypeId?: number;
    saleSourceId?: number;
    status?: string;
    payment?: string;
    /** Which date the range measures: order, invoice or estimatedDelivery. */
    dateField?: string;
    from?: string;
    to?: string;
    productId?: number;
    lineStatusId?: number;
    brandId?: number;
    taxId?: number;
    supplierId?: number;
    commission?: boolean;
    /** The supplier's commission model, not the line's: Auto, OnRequest or None. */
    commissionModel?: string;
    /** The order's own commission status. */
    commissionStatus?: CommissionStatus;
    sizeTypeId?: number;
    sizeId?: number;
    categoryGroupId?: number;
    categoryId?: number;
    categorySubId?: number;
    categorySubChildId?: number;
    preOrder?: boolean;
    dropShip?: boolean;
    /** The legacy soft delete. False is the explicit ask for the deleted orders. */
    active?: boolean;
    sort?: OrderSortKey;
    dir?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
    /**
     * Every live line of each order rather than the four the thumbnails draw. The export asks for
     * it so its products column is complete (ALS-223); the table never does.
     */
    allLines?: boolean;
}

/*
  The order detail page (ALS-59, ALS-60, ALS-61): one order, whole, and the three writes it makes
  without moving money. Every figure is the one stored on the order; nothing here recomputes one.
*/

/**
 * What Cancel order does to this order: delete an unconfirmed one nobody has paid on, cancel a
 * confirmed one or one with money on it (ALS-259).
 */
export type OrderCancelOutcome = 'Delete' | 'Cancel';

/** Why Cancel order will not, the order's reasons first and then the person's. */
export type OrderCancelRefusal = 'Deleted' | 'AlreadyCancelled' | 'Completed' | 'NotPermitted';

/**
 * How Edit line draws a line: the whole line while it is Pending confirmation (ALS-259), status
 * and delivery once it is confirmed, or its status alone while a return or a cancellation is
 * under way.
 */
export type OrderLineEditShape = 'Open' | 'StatusAndDelivery' | 'StatusOnly';

/** Why an order's lines and fees are fixed: it is deleted, cancelled, or money has been taken (ALS-259). */
export type OrderMoneyLock = 'Deleted' | 'Cancelled' | 'MoneyTaken';

/** A line as the line editor holds it, the rows it chose from (ALS-259). */
export interface OrderLineEdit {
    sizeId: number | null;
    colourId: number | null;
    taxId: number;
    countryId: number | null;
    supplierId: number;
    /** An ISO day: when a pre-ordered piece becomes available. */
    availableFrom: string | null;
    /** Pending confirmation or Awaiting payment, on an order nothing has been paid on. */
    removable: boolean;
}

/** What Edit order's fees start from, and whether the order's money can still change (ALS-259). */
export interface OrderMoneyFacts {
    locked: OrderMoneyLock | null;
    shippingTaxId: number;
    dutiesTaxId: number;
    aListFeeUnit: AListFeeUnit;
    /** Money in the order's currency, or a percentage of the products before tax. */
    aListFee: number;
    /** The card fee's rate on an order this admin created; null on one the old portal raised. */
    cardFeePercentage: number | null;
}

export interface OrderClient {
    id: number;
    name: string;
    country: string | null;
    relationshipManager: string | null;
    /** With its dialling code in front. */
    phone: string | null;
    email: string | null;
    /** In pounds; null for a client who has never had a wallet entry, which is not a zero balance. */
    walletBalance: number | null;
    /** The client's saved allergies, for the strip above the order (ALS-393). */
    allergies: CustomerAllergies;
}

export interface OrderAddress {
    id: number;
    /** One part per entry, as the legacy invoice composes it. */
    lines: string[];
    /** Removed from the client's book since; it is still where the order went. */
    removed: boolean;
}

export interface OrderOccasion {
    id: number;
    occasion: string;
    relation: string | null;
    /** An ISO day. */
    date: string;
}

export interface OrderCancellation {
    outcome: OrderCancelOutcome;
    reason: string;
    by: string | null;
    on: string;
}

export interface OrderLineStatusOption {
    status: OrderLineStatus;
    /** Whether arriving at this status can put the piece in the client's wardrobe. */
    allowsWardrobe: boolean;
    /**
     * The per-person switch this status needs and the person does not have (ALS-522): a line
     * cancelled takes Cancel the order, a line returned or refunded takes Return the order. Kept in
     * the list, refused, with the reason under the field. Null or absent when it may be chosen.
     */
    switchedOff?: 'CancelOrders' | 'ProcessReturns' | null;
}

/** One live line of the order: the Items table's columns, the expanded line's facts, and what editing it allows. */
export interface OrderLine {
    id: number;
    productId: number;
    name: string;
    brand: string | null;
    code: string | null;
    category: string | null;
    thumbnailUrl: string | null;
    supplier: string | null;
    /** "38 · Black"; null for a service. */
    variant: string | null;
    qty: number;
    unitPrice: number;
    /** As stored: the line's total, tax included. */
    lineTotal: number;
    costPrice: number;
    discountPercentage: number | null;
    commission: boolean;
    commissionRate: number | null;
    aListFee: number;
    tax: string | null;
    taxIncluded: boolean;
    taxAmount: number;
    totalBeforeTax: number;
    margin: number;
    origin: string | null;
    preOrder: boolean;
    dropShip: boolean;
    returnable: boolean;
    isService: boolean;
    status: OrderLineStatus;
    /** What mStatus calls the status, for a row the design has no name for. */
    statusName: string;
    tone: OrderLineTone;
    tracking: string | null;
    /** ISO days, all four. */
    estimatedDelivery: string | null;
    actualDelivery: string | null;
    neededBy: string | null;
    followUpDate: string | null;
    deliveryComment: string | null;
    reason: string | null;
    /** In the wardrobe by the rule the client's app reads: the status allows it and the line's flag says so. */
    inWardrobe: boolean;
    /** The line's own flag, which Edit line's In wardrobe edits. */
    showInWardrobe: boolean;
    /** The Xero sales account the line posts to (ALS-386): its own, else its product's. */
    accountId: number | null;
    /** "200 · Sales - Goods Sold". */
    account: string | null;
    /** The order is invoiced: its invoice keeps the account each line was issued with. */
    accountLocked: boolean;
    shape: OrderLineEditShape;
    /** Handed back on a save, so a change made meanwhile is not overwritten. */
    revisedOn: string | null;
    edit: OrderLineEdit;
}

export interface OrderRecord {
    id: number;
    orderNo: string;
    orderDate: string;
    createdBy: string | null;
    saleSource: NamedRow | null;
    status: OrderStatus;
    statusName: string;
    /** The legacy soft delete. A deleted order opens read only. */
    deleted: boolean;
    /** Whether the client's app lists it. */
    confirmed: boolean;
    payment: PaymentStatus;
    /** Any money taken: what locks the order date and refuses a cancel. */
    moneyTaken: boolean;
    invoiceNo: string | null;
    /** An ISO day. */
    invoiceDate: string | null;
    invoiceType: NamedRow | null;
    /** Order.ReferenceNo, the Xero invoice number staff type by hand while the sync is down. */
    xeroReference: string | null;
    /** Pushed to Xero by the legacy sync, which has pushed nothing since November 2025. */
    xeroSynced: boolean;
    currency: string;
    exchangeRate: number;
    net: number;
    netBase: number | null;
    estimatedMargin: number;
    estimatedMarginBase: number | null;
    /** Null for an order none of whose products earns commission. */
    commission: OrderCommission | null;
    /** Wallet credit applied at creation; null for an order the old portal raised, which kept no record of it. */
    walletApplied: number | null;
    shippingFee: number;
    duties: number;
    cardFee: number;
    client: OrderClient;
    shippingAddress: OrderAddress | null;
    billingAddress: OrderAddress | null;
    occasion: OrderOccasion | null;
    /** An ISO day. */
    followUpDate: string | null;
    externalNote: string | null;
    remarks: string | null;
    assignedTo: NamedRow[];
    tracking: string[];
    cancellation: OrderCancellation | null;
    cancel: { outcome: OrderCancelOutcome | null; refusal: OrderCancelRefusal | null };
    /** Order editing permission, on a live order. */
    mayEdit: boolean;
    lineStatusOptions: OrderLineStatusOption[];
    revisedOn: string | null;
    lines: OrderLine[];
    /** The Instalments & invoicing tab and the balance banner (ALS-28). */
    payments: OrderPayments;
    money: OrderMoneyFacts;
    /** The cancelled order this one was raised again from (ALS-260). */
    reraisedFrom: OrderLink | null;
    /** The order this cancelled one was raised again as. */
    reraisedAs: OrderLink | null;
    /** Cancelled or deleted, not raised again yet, and this person may edit orders. */
    mayReraise: boolean;
}

/** Another order, by its id and number. */
export interface OrderLink {
    id: number;
    orderNo: string;
}

/** What New order opens with to raise a cancelled order again (ALS-260). */
export interface OrderReraise {
    orderId: number;
    orderNo: string;
    customerId: number;
    occasionId: number | null;
    salesTeamId: number | null;
    saleSourceId: number;
    currencyId: number;
    exchangeRate: number;
    shippingAddressId: number;
    billingAddressId: number;
    externalNote: string | null;
    remarks: string | null;
    shippingFee: number;
    shippingTaxId: number;
    duties: number;
    dutiesTaxId: number;
    aListFeeUnit: AListFeeUnit;
    aListFee: number;
    lines: OrderLineInput[];
}

/** How a payment the team records reached The A List. Wallet is credit the client already holds. */
export type PaymentMethod = 'BankTransfer' | 'Card' | 'Wallet';

/** Single, a deposit invoice's two halves, or one instalment of a plan stored when the order was created. */
export type InstalmentKind = 'Single' | 'Deposit' | 'Balance' | 'Instalment';

export type InstalmentStatus = 'Due' | 'PartlyPaid' | 'Paid';

/** Why Record payment is refused, said on the button before anybody presses it. */
export type PaymentRecordRefusal = 'NotPermitted' | 'Deleted' | 'Cancelled' | 'NothingOutstanding';

/** One row of the schedule. Amounts are in the order's currency. */
export interface OrderInstalment {
    number: number;
    kind: InstalmentKind;
    amount: number;
    /** An ISO day; null where the legacy kept no date. */
    dueOn: string | null;
    paymentType: InstalmentPaymentType;
    cardFee: number;
    paid: number;
    status: InstalmentStatus;
}

/**
 * Where an order's payments stand, worked out by the server: a single payment or a deposit invoice
 * from the legacy's columns, or the plan stored when this admin created the order (ALS-361).
 */
export interface OrderPayments {
    schedule: OrderInstalment[];
    /** The money recorded as taken. */
    received: number;
    /** What is still owed; zero once the order is marked paid. */
    outstanding: number;
    /** Money recorded above the net. */
    overpaid: number;
    /** What an order marked paid in full was never recorded as receiving. */
    short: number;
    /** The first instalment not yet paid: its unpaid part, its date, and which of how many it is. */
    next: { amount: number; dueOn: string | null; instalment: number; of: number } | null;
    /** A deposit invoice's deposit, and when it arrived once it has (an ISO date-time). */
    deposit: { amount: number; paid: boolean; receivedOn: string | null } | null;
    /** Paid, and every piece since refunded, on an order that was not cancelled. */
    refundedInFull: boolean;
    /** The client's wallet in the order's currency: the most a payment from the wallet may take. */
    walletAvailable: number;
    totalBeforeTax: number;
    /** The products' tax and the VAT inside each fee. */
    totalTax: number;
    aListFee: number;
    recordRefusal: PaymentRecordRefusal | null;
    /** Every payment taken against the order, oldest first, with who recorded it and when. */
    recorded: OrderPaymentEntry[];
    /**
     * The plan stored when the order was created, which Edit payment details changes; null without
     * one. Its card fee rate is null when the team typed the fee, which then follows the card share.
     */
    plan: { split: InstalmentSplit; cardFeePercentage: number | null } | null;
}

/**
 * How a logged payment came in. Wallet is credit the client already held, which is also how the
 * old portal recorded its payments; App is the client paying in the app; CreditNote is part of a
 * credit note applied to the order's invoice (ALS-29); Xero is a payment read back from Xero (ALS-70).
 */
export type PaymentEntryMethod = 'BankTransfer' | 'Card' | 'Wallet' | 'App' | 'CreditNote' | 'Xero';

/** One payment in the order's log. Amounts are in the order's currency. */
export interface OrderPaymentEntry {
    /** The payment's own id, which its Xero state is found by (ALS-456). */
    id: number;
    /** An ISO date-time. */
    on: string;
    /** What went against the order. */
    amount: number;
    /** What came in above that and went to the client's wallet as credit. */
    toWallet: number;
    method: PaymentEntryMethod;
    /** The staff member who recorded it; null when the client paid in the app. */
    by: string | null;
    /** The credit notes it came off, when the method is CreditNote: one, or several joined oldest first. */
    creditNote?: string | null;
    /** When the method is Xero and only part of the payment was recorded here: its whole amount in Xero (ALS-70). */
    xeroAmount?: number | null;
    /** When the method is CreditNote: wallet credit no credit note backs paid the rest. */
    withWalletCredit?: boolean;
}

/** The instalments still to pay on a plan, as Edit payment details sends them (ALS-361). */
export interface OrderPlanInput {
    split: InstalmentSplit;
    /** In order, after the paid ones. Amounts are read only on a custom split. */
    open: { amount: number; paymentType: InstalmentPaymentType; dueDate: string | null }[];
    revisedOn: string | null;
}

/** What saving the plan would do, worked out by the server without saving it. */
export interface OrderPlanPreview {
    open: { amount: number; paymentType: InstalmentPaymentType; cardFee: number }[];
    cardFee: number;
    /** The order total, which moves with the card fee. */
    net: number;
    /** What the paid instalments collected. */
    paid: number;
    /** What the instalments still to pay must collect between them. */
    stillToPay: number;
    scheduled: number;
    /** Scheduled less what the plan collects: above zero it is over, below short. Saving needs zero. */
    difference: number;
}

/** One payment, as Record payment sends it. */
export interface OrderPaymentInput {
    amount: number;
    method: PaymentMethod;
    revisedOn: string | null;
    /** Record it in Xero too, against the order's invoice (ALS-456); never for wallet credit. */
    sendToXero: boolean;
}

export interface OrderCommission {
    status: CommissionStatus;
    /** What the suppliers owe, in the order's currency; null when a product has no rate on the order date. */
    due: number | null;
}

/** What Edit order selects over, asked for when the drawer opens. */
export interface OrderOptions {
    saleSources: NamedRow[];
    staff: NamedRow[];
    occasions: OrderOccasion[];
    addresses: OrderAddress[];
}

/** A client New order can be raised for, with what tells them apart from a namesake. */
export interface NewOrderClientOption {
    id: number;
    name: string;
    email: string | null;
    /** With its dialling code in front. */
    phone: string | null;
    country: string | null;
}

/** A currency an order may be raised in. */
export interface OrderCurrency {
    id: number;
    code: string;
    symbol: string;
    /**
     * The rate the last order in this currency was raised at, which a typed rate is held against
     * (ALS-26); null for pounds, whose rate is one, and for a currency no order has used.
     */
    lastRate?: number | null;
}

/** What New order selects over, asked for when the page opens (ALS-62). */
export interface NewOrderOptions {
    /** The live clients, by name. */
    clients: NewOrderClientOption[];
    saleSources: NamedRow[];
    /** The staff who can sign in. */
    salesTeam: NamedRow[];
    currencies: OrderCurrency[];
    /** The register's default: GBP. */
    defaultCurrencyId: number;
}

/** What choosing a client on New order fills in. */
export interface NewOrderClient {
    client: OrderClient;
    occasions: OrderOccasion[];
    addresses: OrderAddress[];
    /** The default shipping address, else the first in the book; null when the book is empty. */
    shippingAddressId: number | null;
    /** The default billing address, else the shipping one. */
    billingAddressId: number | null;
}

/** A row of the tax register a line (ALS-63) or one of the order's fees (ALS-27) may carry. */
export interface LineTax {
    id: number;
    name: string;
    /** 20 for VAT 20 %. */
    percentage: number;
    /** The Zero rated row, where the fees start. Margin scheme is zero percent too, so it is named. */
    zeroRated: boolean;
}

/** How the team set an order's A-List fee: money, or a percentage of the products before tax (ALS-26). */
export const A_LIST_FEE_UNITS = ['Amount', 'Percentage'] as const;
export type AListFeeUnit = (typeof A_LIST_FEE_UNITS)[number];

/** How an instalment is paid; only a card one carries the card fee (ALS-25). */
export const INSTALMENT_PAYMENT_TYPES = ['Card', 'BankTransfer'] as const;
export type InstalmentPaymentType = (typeof INSTALMENT_PAYMENT_TYPES)[number];

/** How a plan's amounts are set: split evenly by the server, or typed by the team (ALS-25). */
export const INSTALMENT_SPLITS = ['Equal', 'Custom'] as const;
export type InstalmentSplit = (typeof INSTALMENT_SPLITS)[number];

/** What an order's money is worked out from. Tax is a register row, the wallet the client's own. */
/** One line as the line editor sends it: New order, Add product, and a Pending line repriced (ALS-259). */
export interface OrderLineInput {
    productId: number;
    quantity: number;
    sizeId: number | null;
    colourId: number | null;
    taxId: number;
    taxIncluded: boolean;
    countryId: number | null;
    dropShip: boolean;
    preOrder: boolean;
    neededBy: string | null;
    availableFrom: string | null;
    salePrice: number;
    costPrice: number;
    supplierId: number;
    commission: boolean;
    discount: number | null;
    aListFee: number;
    /** The Xero sales account the line posts to. */
    accountId: number | null;
}

/** New order as Create order sends it (ALS-361): everything the form holds. */
export interface OrderCreateInput {
    /** The form's own key: sending the same form again opens the order it made. */
    requestKey: string;
    customerId: number;
    /** An ISO day. */
    orderDate: string;
    occasionId: number | null;
    salesTeamId: number | null;
    saleSourceId: number;
    currencyId: number;
    exchangeRate: number;
    status: OrderStatus;
    shippingAddressId: number;
    billingAddressId: number;
    followUpDate: string | null;
    xeroReference: string | null;
    externalNote: string | null;
    remarks: string | null;
    lines: OrderLineInput[];
    shippingFee: number;
    shippingTaxId: number | null;
    duties: number;
    dutiesTaxId: number | null;
    aListFeeUnit: AListFeeUnit;
    aListFee: number;
    typedCardFee: TypedCardFeeInput | null;
    cardFeeTaxId: number | null;
    plan: {
        split: InstalmentSplit;
        instalments: {
            amount: number;
            paymentType: InstalmentPaymentType;
            dueDate: string | null;
        }[];
    } | null;
    applyWallet: boolean;
    /** The cancelled order this one raises again (ALS-260); left out for a new order. */
    reraisedFromOrderId?: number | null;
}

export interface OrderMoneyInput {
    customerId: number | null;
    exchangeRate: number;
    lines: {
        quantity: number;
        salePrice: number;
        costPrice: number;
        taxId: number;
        taxIncluded: boolean;
        aListFee: number;
    }[];
    shippingFee: number;
    shippingTaxId: number | null;
    duties: number;
    dutiesTaxId: number | null;
    aListFeeUnit: AListFeeUnit;
    aListFee: number;
    /** The card fee as the team typed it; null to have it worked out at the configured rate. */
    typedCardFee: TypedCardFeeInput | null;
    cardFeeTaxId: number | null;
    /** The instalment plan, or null for one payment. Amounts are read only on a custom split. */
    plan: MoneyPlanInput | null;
    applyWallet: boolean;
}

/** An instalment plan as the totals read it: how its amounts are set, and how each row is paid. */
export interface MoneyPlanInput {
    split: InstalmentSplit;
    instalments: { amount: number; paymentType: InstalmentPaymentType }[];
}

/**
 * A card fee the team typed (ALS-383): the amount, and the plan as it stood when it was typed,
 * whose card share the server reads, so the fee follows the share from there.
 */
export interface TypedCardFeeInput {
    amount: number;
    plan: MoneyPlanInput | null;
}

/**
 * An order's money as the server works it out (ALS-26), the one calculation the totals card, the
 * instalments and the created order all read. In the order's currency, except the wallet's pounds.
 */
export interface OrderMoney {
    totalBeforeTax: number;
    productsTax: number;
    totalAfterTax: number;
    shippingFee: number;
    shippingTax: number;
    duties: number;
    dutiesTax: number;
    aListFee: number;
    /** The same fee as a percentage of the products before tax; null while there are none. */
    aListFeeAsPercentage: number | null;
    lineFees: number;
    aListFeeTax: number;
    cardShare: number;
    cardFee: number;
    cardFeeTax: number;
    /** What the configured rate gives for the same card share: what Recalculate puts back. */
    calculatedCardFee: number;
    /** The configured rate the card fee is worked out at. */
    cardFeePercentage: number;
    tax: number;
    base: number;
    net: number;
    walletApplied: number;
    walletAppliedPounds: number;
    walletLeftPounds: number;
    /** The net less the wallet credit: what the client still owes. */
    due: number;
    costs: number;
    margin: number;
    marginPounds: number;
    marginPercentage: number;
    /** The plan's rows with what each collects and the card fee inside it; empty with no plan. */
    instalments: { amount: number; paymentType: InstalmentPaymentType; cardFee: number }[];
    /** What the rows add up to. */
    scheduled: number;
    /** What is due and no row collects: above zero a custom split is short, below zero over. */
    unscheduled: number;
}

/** A supplier a line may go to, and whether it still waits on approval. */
export interface LineSupplier {
    id: number;
    name: string;
    pendingApproval: boolean;
}

/** The registers every order line chooses from. */
export interface OrderLineLists {
    /** The register's live rows only. */
    taxes: LineTax[];
    /** Every supplier a new line may go to: all but the blacklisted. */
    suppliers: LineSupplier[];
    /** The Xero sales accounts a line may post to, the ones used daily first (ALS-386). */
    accounts: XeroAccountChoice[];
}

/** The commission a supplier would earn a line of a product on the order date. */
export interface LineCommission {
    supplierId: number;
    /** A percentage: 18 for 18%. */
    rate: number;
}

/** A product as a new order line starts from it. */
export interface OrderLineProduct {
    id: number;
    name: string;
    code: string;
    brandId: number;
    brand: string | null;
    category: string | null;
    imageUrl: string | null;
    price: number;
    costPrice: number;
    taxId: number;
    taxIncluded: boolean;
    countryId: number | null;
    supplierId: number;
    supplier: string | null;
    sizes: NamedRow[];
    colours: NamedRow[];
    commissions: LineCommission[];
    /** The Xero sales account a line of it starts on: its own while offered, else Goods Sold. */
    accountId: number | null;
}

/** Why a lookbook the client sees cannot give an order a product (ALS-64). */
export type LookbookUnavailable = 'NotLive' | 'NoProducts';

/** A lookbook the client sees, as Add from lookbook offers it. */
export interface NewOrderLookbook {
    id: number;
    name: string;
    category: string | null;
    /** Just For You: made for this client alone. */
    personal: boolean;
    effectiveDate: string;
    productCount: number;
    unavailable: LookbookUnavailable | null;
}

/** Why a product tagged in a lookbook cannot go on an order. */
export type LookbookProductUnavailable = 'Archived' | 'NotInCatalogue';

export interface LookbookProduct {
    pinId: number;
    productId: number;
    name: string;
    brand: string | null;
    code: string | null;
    category: string | null;
    price: number | null;
    imageUrl: string | null;
    unavailable: LookbookProductUnavailable | null;
}

/** A page of a pages book, or the whole of a products book (page null). */
export interface LookbookProductGroup {
    page: number | null;
    products: LookbookProduct[];
}

/** A file on an order's comment, served from the asset store (ALS-65). */
export interface OrderCommentFile {
    id: number;
    fileName: string;
    contentType: string;
    byteSize: number;
    url: string;
}

/** One internal comment: who wrote what and when, and its files. */
export interface OrderComment {
    id: number;
    text: string;
    by: string | null;
    /** ISO, the database's London clock. */
    on: string;
    files: OrderCommentFile[];
}

/** Edit order's fields. Every one is sent; the server writes only what changed. */
export interface OrderUpdateInput {
    status: OrderStatus;
    /** An ISO day. The time of day the order was raised is kept. */
    orderDate: string;
    saleSourceId: number;
    /** Every live line goes to this person; null leaves the lines as they are. */
    assignedToId: number | null;
    occasionId: number | null;
    followUpDate: string | null;
    shippingAddressId: number;
    billingAddressId: number;
    xeroReference: string | null;
    externalNote: string | null;
    remarks: string | null;
    revisedOn: string | null;
    /** Only on an order with commission; null leaves the status as it is. */
    commissionStatus: CommissionStatus | null;
    /** Shipping, duties and the A-List fee (ALS-259); sent as stored they change nothing. */
    fees: OrderFeesInput | null;
}

/** Edit order's three money fields. A tax row left out is Zero rated, as on New order. */
export interface OrderFeesInput {
    shippingFee: number;
    shippingTaxId: number | null;
    duties: number;
    dutiesTaxId: number | null;
    aListFeeUnit: AListFeeUnit;
    aListFee: number;
}

export interface OrderLineUpdateInput {
    /** Only when it was changed; null leaves the line's status as it is, whatever it is by now. */
    status: OrderLineStatus | null;
    tracking: string | null;
    estimatedDelivery: string | null;
    actualDelivery: string | null;
    deliveryComment: string | null;
    followUpDate: string | null;
    /** Null keeps the line's flag, or puts the piece in the wardrobe when it arrives at a status that allows it. */
    inWardrobe: boolean | null;
    reason: string | null;
    revisedOn: string | null;
    /** The whole line from the line editor, on a line still Pending confirmation (ALS-259). */
    details?: OrderLineInput | null;
    /** Only when it was changed: the line's Xero sales account. */
    accountId?: number | null;
}

/**
 * Bulk edit (ALS-261): what to set on the selected lines. A field left null is left alone on
 * every line, so a bulk edit sets values and never clears one.
 */
export interface OrderLinesBulkInput {
    lines: { id: number; revisedOn: string | null }[];
    status: OrderLineStatus | null;
    tracking: string | null;
    estimatedDelivery: string | null;
    actualDelivery: string | null;
    deliveryComment: string | null;
    followUpDate: string | null;
    inWardrobe: boolean | null;
    reason: string | null;
}

/** A line the bulk edit left wholly or partly alone, and why. */
export interface OrderBulkSkip {
    id: number;
    name: string;
    reasons: string[];
}

export interface OrderBulkResult {
    record: OrderRecord;
    /** The lines the edit changed; one it left wholly alone is not counted. */
    changed: number;
    skipped: OrderBulkSkip[];
}

export interface OrderCancelInput {
    reason: string;
    revisedOn: string | null;
}

const root = '/api/orders';

/** The export walks the register in pages of the server's maximum. */
const EXPORT_PAGE_SIZE = 500;

function queryString(query: OrderListQuery): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

export const ordersApi = {
    list: (query: OrderListQuery) => api<OrderPage>(`${root}${queryString(query)}`),
    /**
     * Every order the query matches, for the export: walked in pages of the server's maximum,
     * each with every live line rather than the table's four thumbnails, so the products column
     * can name them all (ALS-223).
     */
    all: async (query: OrderListQuery): Promise<OrderListItem[]> => {
        const items: OrderListItem[] = [];
        for (let page = 1; ; page++) {
            const chunk = await ordersApi.list({
                ...query,
                page,
                pageSize: EXPORT_PAGE_SIZE,
                allLines: true,
            });
            items.push(...chunk.items);
            if (items.length >= chunk.total || chunk.items.length === 0) break;
        }
        return items;
    },
    /** One order's lines and facts, asked for when its row is opened. */
    overview: (id: number) => api<OrderOverview>(`${root}/${id}/overview`),
    lookups: () => api<OrderLookups>(`${root}/lookups`),
    /** The 13,297 products that appear on an order. Asked for only once the drawer is opened. */
    products: () => api<NamedRow[]>(`${root}/products`),
    /** One order, whole, for its detail page. */
    record: (id: number) => api<OrderRecord>(`${root}/${id}`),
    /** What Edit order selects over. */
    options: (id: number) => api<OrderOptions>(`${root}/${id}/options`),
    /** What New order selects over. */
    newOptions: () => api<NewOrderOptions>(`${root}/new/options`),
    /** What choosing a client on New order fills in. */
    newClient: (customerId: number) => api<NewOrderClient>(`${root}/new/clients/${customerId}`),
    /** The number the next order on a date would get. A preview: nothing is reserved. */
    nextNumber: (orderDate: string) =>
        api<{ orderNo: string }>(`${root}/next-number?orderDate=${encodeURIComponent(orderDate)}`),
    /** The tax register and the suppliers a line chooses from. */
    lineLists: () => api<OrderLineLists>(`${root}/line-lists`),
    /** An order's totals, tax, credit and margin, worked out without saving anything. */
    money: (input: OrderMoneyInput) =>
        api<OrderMoney>(`${root}/money`, { method: 'POST', body: input }),
    /** Products as new order lines start from them, with commission as of the order date. */
    lineProducts: (ids: readonly number[], orderDate: string) =>
        api<OrderLineProduct[]>(
            `${root}/line-products?ids=${ids.join(',')}&orderDate=${encodeURIComponent(orderDate)}`,
        ),
    /** The lookbooks a client sees, for Add from lookbook. */
    newLookbooks: (customerId: number) =>
        api<NewOrderLookbook[]>(`${root}/new/lookbooks?customerId=${customerId}`),
    /** One lookbook's products, grouped by page. */
    newLookbookProducts: (lookbookId: number, customerId: number) =>
        api<LookbookProductGroup[]>(
            `${root}/new/lookbooks/${lookbookId}/products?customerId=${customerId}`,
        ),
    /** The order's internal comments, newest first. */
    comments: (id: number) => api<OrderComment[]>(`${root}/${id}/comments`),
    /** Posts a comment; its id comes back so its files can follow. */
    addComment: (id: number, text: string) =>
        api<{ id: number; comments: OrderComment[] }>(`${root}/${id}/comments`, {
            method: 'POST',
            body: { text },
        }),
    /** Attaches one file to a comment. PDF, JPG or PNG, 10 MB at most. */
    attachCommentFile: (id: number, commentId: number, file: File) => {
        const form = new FormData();
        form.append('file', file, file.name);
        return api<OrderComment[]>(`${root}/${id}/comments/${commentId}/files`, {
            method: 'POST',
            body: form,
        });
    },
    update: (id: number, input: OrderUpdateInput) =>
        api<OrderRecord>(`${root}/${id}`, { method: 'PUT', body: input }),
    updateLine: (id: number, lineId: number, input: OrderLineUpdateInput) =>
        api<OrderRecord>(`${root}/${id}/lines/${lineId}`, { method: 'PUT', body: input }),
    /** What New order opens with to raise a cancelled order again (ALS-260). */
    reraise: (id: number) => api<OrderReraise>(`${root}/${id}/reraise`),
    /** Takes a line off an order nothing has been paid on (ALS-259). */
    removeLine: (id: number, lineId: number, revisedOn: string | null) =>
        api<OrderRecord>(`${root}/${id}/lines/${lineId}/remove`, {
            method: 'POST',
            body: { revisedOn },
        }),
    /** Sets several lines' status and delivery together (ALS-261). */
    bulkLines: (id: number, input: OrderLinesBulkInput) =>
        api<OrderBulkResult>(`${root}/${id}/lines/bulk`, { method: 'POST', body: input }),
    /** Adds the Add product drawers' lines to an order nothing has been paid on (ALS-259). */
    addLines: (id: number, lines: OrderLineInput[], revisedOn: string | null) =>
        api<OrderRecord>(`${root}/${id}/lines`, { method: 'POST', body: { lines, revisedOn } }),
    cancel: (id: number, input: OrderCancelInput) =>
        api<OrderRecord>(`${root}/${id}/cancel`, { method: 'POST', body: input }),
    /** Records money received against the order, through the client's wallet (ALS-28). */
    recordPayment: (id: number, input: OrderPaymentInput) =>
        api<OrderRecord>(`${root}/${id}/payments`, { method: 'POST', body: input }),
    /** Creates the order New order holds (ALS-361). The same form sent again answers with the order it made. */
    create: (input: OrderCreateInput) =>
        api<{ id: number; orderNo: string }>(root, { method: 'POST', body: input }),
    /** What Edit payment details would save, worked out without saving it. */
    planPreview: (id: number, input: OrderPlanInput) =>
        api<OrderPlanPreview>(`${root}/${id}/plan/preview`, { method: 'POST', body: input }),
    savePlan: (id: number, input: OrderPlanInput) =>
        api<OrderRecord>(`${root}/${id}/plan`, { method: 'PUT', body: input }),
};
